/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarClock, DollarSign, Sparkles, QrCode, Scissors, CheckCircle2, Clock, RefreshCw, X, Download } from "lucide-react";
import toast from 'react-hot-toast';
import dashboardApi from '../../services/dashboardApi';
import BookingsCalendar from '../../components/admin/BookingsCalendar';
import { logger } from '../../utils/logger';
import { subscribeDashboardRefresh } from '../../utils/dashboardRefreshBus';
import { useAuth } from '../../context/AuthContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { resolveUserShopId } from '../../utils/adminShopContext';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip
} from "recharts";

/**
 * Hairchitech Dashboard (single-file component)
 * - TailwindCSS (dark & light) respected via utility classes
 * - Recharts for data viz
 * - Lucide icons
 * - Subtle motion for micro-interactions
 *
 * Wiring notes:
 *  - Replace the demo data with live data from your API.
 *  - For gold brand, we use #7d6633 (as requested). Adjust via ACCENT variable.
 */

export const ACCENT = "#7d6633"; // Brand gold

export const COLORS = [ACCENT, "#9ca3af", "#60a5fa", "#34d399", "#fbbf24"]; // gold + neutrals for charts

// --- Small UI helpers ---
export const Card = ({ children, className = "" }) => (
  <div
    className={
      "rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl " +
      "" + className
    }
  >
    {children}
  </div>
);

export const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-2.5 md:p-3 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

export const Stat = ({ label, value, delta, icon }) => (
  <motion.div whileHover={{ y: -2, scale: 1.01 }} transition={{ duration: 0.2 }} className="group h-full">
    <Card className="p-2 md:p-2.5 xl:p-3 border-l-2 xl:border-l-4 h-full min-h-[104px]" style={{ borderLeftColor: ACCENT }}>
      <div className="h-full flex flex-col items-center justify-center text-center gap-1.5">
        {/* Icon centered */}
        <div className="h-8 w-8 md:h-9 md:w-9 xl:h-10 xl:w-10 rounded-lg border border-white/10 dark:border-white/10 flex items-center justify-center group-hover:scale-105 group-hover:rotate-3 transition-all duration-300" style={{ background: `${ACCENT}25` }}>
          {React.cloneElement(icon, {
            className: "h-4 w-4 md:h-[18px] md:w-[18px] xl:h-5 xl:w-5",
            style: { color: ACCENT },
          })}
        </div>

        {/* Label + value centered */}
        <div className="w-full min-w-0">
          <p className="text-[10px] md:text-[11px] xl:text-xs uppercase tracking-wide font-semibold text-zinc-500 dark:text-zinc-400 leading-tight truncate">
            {label}
          </p>
          <div className="mt-0.5 flex items-baseline justify-center gap-1">
            <span className="text-lg md:text-xl xl:text-2xl font-bold text-zinc-900 dark:text-zinc-100 truncate max-w-full">
              {value}
            </span>
            {delta && (
              <span className={`text-[10px] md:text-[11px] font-semibold ${delta.startsWith("+") ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                {delta}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  </motion.div>
);

export const Progress = ({ value }) => (
  <div className="w-full h-2 bg-zinc-200/70 dark:bg-zinc-800 rounded-full overflow-hidden">
    <div className="h-full" style={{ width: `${value}%`, background: ACCENT }} />
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    Pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    Canceled: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`}>
      {status}
    </span>
  );
};

export const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-semibold tracking-wide text-zinc-700 dark:text-zinc-200">{children}</h2>
);

// Sort helper: newest-created bookings first (fallback to id, then start time)
const bookingSortKey = (b) => {
  const created =
    b?.created_at ??
    b?.createdAt ??
    b?.created_utc ??
    b?.created ??
    b?.inserted_at;

  const createdTs = created ? Date.parse(created) : Number.NaN;
  if (Number.isFinite(createdTs)) return createdTs;

  const id = Number(b?.id);
  if (Number.isFinite(id)) return id;

  const start = b?.start_utc ?? b?.start_local ?? b?.start_time;
  const startTs = start ? Date.parse(start) : Number.NaN;
  return Number.isFinite(startTs) ? startTs : 0;
};

const sortBookingsNewestFirst = (items) =>
  Array.isArray(items) ? [...items].sort((a, b) => bookingSortKey(b) - bookingSortKey(a)) : [];


export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { user: adminUser, loading: adminLoading, shopId: adminShopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const shopId = adminShopId || resolveUserShopId(adminUser, user);
  
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    stats: { totalBookings: 0, totalRevenue: 0, activeBarbers: 0, bookingsTrend: 0, revenueTrend: 0 },
    bookingsOverTime: [],
    revenueVsBookings: [],
    sourceMix: [],
    barberPerformance: [],
    todaySchedule: [],
    recentBookings: [],
    aiActivity: [],
    systemAlerts: []
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRange, setSelectedRange] = useState('7D');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Load dashboard data
  const loadDashboardData = useCallback(async (showRefresh = false) => {
    try {
      if (!shopId) {
        logger.warn('Dashboard load skipped: missing shop_id');
        return;
      }
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      logger.debug('Loading dashboard data...');

      const [
        stats,
        bookingsOverTime,
        revenueVsBookings,
        sourceMix,
        barberPerformance,
        todaySchedule,
        recentBookings,
        aiActivity,
        systemAlerts
      ] = await Promise.all([
        dashboardApi.getDashboardStats(selectedRange.toLowerCase(), customFrom || null, customTo || null, shopId),
        dashboardApi.getBookingsOverTime(selectedRange === '7D' ? 7 : selectedRange === '30D' ? 30 : 1, customFrom || null, customTo || null, shopId),
        dashboardApi.getRevenueVsBookings(selectedRange === '7D' ? 7 : selectedRange === '30D' ? 30 : 1, customFrom || null, customTo || null, shopId),
        dashboardApi.getClientSourceMix(shopId),
        dashboardApi.getBarberPerformance('month', shopId),
        dashboardApi.getTodaySchedule(shopId),
        dashboardApi.getRecentBookings(20, shopId),
        dashboardApi.getAIActivity(),
        dashboardApi.getSystemAlerts()
      ]);

      logger.debug('Dashboard data loaded', {
        bookingsOverTime: bookingsOverTime?.length,
        revenueVsBookings: revenueVsBookings?.length,
        sourceMix: sourceMix?.length,
        barberPerformance: barberPerformance?.length,
        todaySchedule: todaySchedule?.length,
        recentBookings: recentBookings?.length,
      });

      setDashboardData({
        stats: stats || dashboardData.stats,
        bookingsOverTime: Array.isArray(bookingsOverTime) ? bookingsOverTime : [],
        revenueVsBookings: Array.isArray(revenueVsBookings) ? revenueVsBookings : [],
        sourceMix: Array.isArray(sourceMix) ? sourceMix : [],
        barberPerformance: Array.isArray(barberPerformance) ? barberPerformance : [],
        todaySchedule: Array.isArray(todaySchedule) ? todaySchedule : [],
        recentBookings: sortBookingsNewestFirst(recentBookings),
        aiActivity: Array.isArray(aiActivity) ? aiActivity : [],
        systemAlerts: Array.isArray(systemAlerts) ? systemAlerts : []
      });

    } catch (error) {
      logger.error('Failed to load dashboard data', { message: error?.message, status: error?.response?.status });
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, customFrom, customTo, shopId]);

  // Load once and refresh only from explicit booking events.
  useEffect(() => {
    if (adminLoading) {
      return undefined;
    }
    if (shopId) {
      loadDashboardData();
    } else {
      logger.warn('Dashboard load skipped: missing shop_id after admin auth');
      setLoading(false);
    }
    // Listen for booking events
    const handleBookingCreated = (event) => {
      logger.debug('Booking created -> refresh dashboard');
      loadDashboardData(true);
    };
    
    const handleBookingRescheduled = (event) => {
      logger.debug('Booking rescheduled -> refresh dashboard');
      loadDashboardData(true);
    };
    
    const handleBookingReassigned = (event) => {
      logger.debug('Booking reassigned -> refresh dashboard');
      loadDashboardData(true);
    };
    
    const handleBookingCancelled = (event) => {
      logger.debug('Booking cancelled -> refresh dashboard');
      loadDashboardData(true);
    };

    const unsubscribeDashboardRefresh = subscribeDashboardRefresh(() => {
      logger.debug('Dashboard invalidation signal received');
      loadDashboardData(true);
    });
    
    window.addEventListener('bookingCreated', handleBookingCreated);
    window.addEventListener('bookingRescheduled', handleBookingRescheduled);
    window.addEventListener('bookingReassigned', handleBookingReassigned);
    window.addEventListener('bookingCancelled', handleBookingCancelled);
    
    return () => {
      unsubscribeDashboardRefresh();
      window.removeEventListener('bookingCreated', handleBookingCreated);
      window.removeEventListener('bookingRescheduled', handleBookingRescheduled);
      window.removeEventListener('bookingReassigned', handleBookingReassigned);
      window.removeEventListener('bookingCancelled', handleBookingCancelled);
    };
  }, [adminLoading, shopId, loadDashboardData]);

  // Handle range change
  const handleRangeChange = (range) => {
    setSelectedRange(range);
    setShowCustomPicker(false);
    setCustomFrom('');
    setCustomTo('');
  };

  // Handle custom date range changes (dynamic updates)
  const handleCustomFromChange = (date) => {
    setCustomFrom(date);
    if (date && customTo) {
      setSelectedRange('Custom');
    }
  };

  const handleCustomToChange = (date) => {
    setCustomTo(date);
    if (customFrom && date) {
      setSelectedRange('Custom');
    }
  };

  // Manual refresh
  const handleRefresh = () => {
    loadDashboardData(true);
  };

  // Export Recent Bookings to CSV
  const handleExportCSV = () => {
    try {
      const bookings = dashboardData.recentBookings || [];
      
      if (bookings.length === 0) {
        toast.error('No bookings to export');
        return;
      }

      // CSV Headers
      const headers = ['Client', 'Service', 'Barber', 'Date', 'Time', 'Price', 'Status'];
      
      // CSV Rows
      const rows = bookings.map(booking => {
        // Extract price
        let price = 0;
        if (typeof booking.price === 'number') {
          price = booking.price;
        } else if (booking.service?.price_cents) {
          price = booking.service.price_cents / 100;
        } else if (booking.Service?.price_cents) {
          price = booking.Service.price_cents / 100;
        } else if (booking.price_cents) {
          price = booking.price_cents / 100;
        } else if (booking.service_price) {
          price = booking.service_price / 100;
        }

        // Extract date and time
        const dateTime = booking.start_local || booking.start_time || booking.start_utc;
        const date = dateTime ? new Date(dateTime).toLocaleDateString() : 'N/A';
        const time = dateTime ? new Date(dateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A';

        // Extract service name
        const serviceName = typeof booking.service === 'object' 
          ? (booking.service?.name || booking.Service?.name || booking.name || 'Unknown')
          : (booking.Service?.name || booking.service || 'Unknown');

        return [
          booking.client?.name || booking.client?.email || 'Unknown',
          serviceName,
          booking.barber?.name || booking.barber || 'N/A',
          date,
          time,
          `$${price.toFixed(2)}`,
          booking.status || 'N/A'
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma
          const cellStr = String(cell).replace(/"/g, '""');
          return cellStr.includes(',') ? `"${cellStr}"` : cellStr;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `hairchitech-bookings-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${bookings.length} bookings to CSV`);
    } catch (error) {
      console.error('CSV Export Error:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Range controls reused for mobile and desktop placements
  const RangeControls = ({ className = "" }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <button 
        onClick={() => handleRangeChange('Today')}
        className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === 'Today' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}
      >
        {t('admin.today')}
      </button>
      <button 
        onClick={() => handleRangeChange('7D')}
        className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === '7D' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}
      >
        {t('admin.range_7d')}
      </button>
      <button 
        onClick={() => handleRangeChange('30D')}
        className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === '30D' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}
      >
        {t('admin.range_30d')}
      </button>
      <button 
        onClick={() => setShowCustomPicker(!showCustomPicker)}
        className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === 'Custom' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}
      >
        {t('admin.range_custom')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center min-h-96">
          <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="text-lg">{t('admin.loading_dashboard')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="w-full">
        <Card className="p-6 md:p-8">
          <div className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Shop context unavailable
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              The dashboard could not resolve a shop for this account, so tenant-scoped data was not requested.
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex w-fit items-center rounded-lg px-4 py-2 text-sm font-medium text-white"
              style={{ background: ACCENT }}
            >
              Retry
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg
                className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0"
                style={{ color: ACCENT }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              {t('admin.dashboard')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.dashboard_subtitle')}
            </p>
          </div>

          {/* Desktop placement: show range controls on the right */}
          <div className="hidden md:flex flex-shrink-0">
            <RangeControls />
          </div>
        </div>

        {/* Mobile placement: show range controls below, left-aligned */}
        <div className="mt-3 md:hidden">
          <RangeControls className="w-full justify-start hc-range-scroll hc-range" />
        </div>
      </div>

      {/* Calendar Section */}
      <div className="mb-6">
        <SectionTitle>{t('admin.section_calendar_view')}</SectionTitle>
        <Card className="mt-4">
          <CardHeader
            title={t('admin.bookings_calendar')}
            subtitle={t('admin.bookings_calendar_subtitle')}
            icon={<CalendarClock className="h-5 w-5" />}
            action={
              <button
                onClick={() => setIsCalendarOpen((v) => !v)}
                aria-label="Toggle calendar"
                className="px-3 py-1.5 rounded-lg border border-white/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-xs font-medium"
              >
                {isCalendarOpen ? t('admin.calendar_hide') : t('admin.calendar_show')}
              </button>
            }
          />
          {isCalendarOpen && (
            <div className="p-3 md:p-4">
              <BookingsCalendar mode="admin" />
            </div>
          )}
        </Card>
      </div>

      {/* Custom Date Picker */}
      {showCustomPicker && (
        <Card className="mb-6 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.from_date')}
              </label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => handleCustomFromChange(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.to_date')}
              </label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => handleCustomToChange(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
            </div>
            <div className="sm:col-span-1 flex justify-end">
              <button
                onClick={() => setShowCustomPicker(false)}
                className="w-full sm:w-auto px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-300 hover:bg-white/80 dark:hover:bg-zinc-900/80 transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Content */}
      <div className="space-y-6">
        {/* Key Performance Indicators */}
        <div>
          <SectionTitle>{t('admin.section_key_metrics')}</SectionTitle>
          <div className="mt-3 flex flex-nowrap gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 xl:gap-4">
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.stat_total_bookings')} 
                value={dashboardData.stats?.totals?.bookings?.toLocaleString() || "0"} 
                delta={dashboardData.stats.bookingsTrend ? `${dashboardData.stats.bookingsTrend > 0 ? '+' : ''}${dashboardData.stats.bookingsTrend}%` : ""} 
                icon={<CalendarClock />} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.stat_revenue')} 
                value={`$${((dashboardData.stats?.totals?.revenue_cents || 0) / 100).toLocaleString()}`} 
                delta={dashboardData.stats.revenueTrend ? `${dashboardData.stats.revenueTrend > 0 ? '+' : ''}${dashboardData.stats.revenueTrend}%` : ""} 
                icon={<DollarSign />} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.stat_active_barbers')} 
                value={dashboardData.stats?.active_counts?.barbers?.toString() || "0"} 
                delta="" 
                icon={<Scissors />} 
              />
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div>
          <SectionTitle>{t('admin.section_performance_overview')}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mt-4">
          {/* Team Performance */}
          <Card>
            <CardHeader title={t('admin.team_performance')} subtitle="Progress" icon={<Scissors className="h-4 w-4" />} />
            <div className="p-2 max-h-64 overflow-y-auto">
              <div className="space-y-1.5">
                {(dashboardData.barberPerformance || []).slice(0, 5).map((b) => {
                  const pct = Math.min(100, Math.round(((b.completed || b.bookings || 0) / (b.target || 10)) * 100));
                  return (
                    <div key={b.barber_id || b.id || Math.random()} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                      <div className="h-8 w-8 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-semibold text-xs flex-shrink-0" style={{ color: ACCENT }}>
                        {(b.name || b.barber || "B").split(" ").map((n) => n[0]).join("")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="font-medium text-xs text-zinc-900 dark:text-zinc-100 truncate">{b.name || b.barber || "Unknown"}</p>
                        </div>
                        <Progress value={pct} />
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{b.completed || b.bookings || 0}/{b.target || 10}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Today's Schedule */}
          <Card>
            <CardHeader title={t('admin.today_schedule')} subtitle={t('admin.today_schedule_subtitle')} icon={<Clock className="h-4 w-4" />} />
            <div className="p-2 max-h-64 overflow-y-auto">
              {(dashboardData.todaySchedule || []).length > 0 ? (
                <div className="space-y-1.5">
                  {(dashboardData.todaySchedule || []).slice(0, 6).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg border border-black/5 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                      <div className="h-8 w-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: `${ACCENT}20`, color: ACCENT }}>
                        {typeof s.time === 'string' ? s.time : (s.start_time ? new Date(s.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">{s.client?.name || s.client?.email || 'Unknown Client'}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{typeof s.service === 'object' ? (s.service?.name || s.Service?.name || 'Unknown Service') : (s.Service?.name || s.service || 'Unknown Service')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">{t('admin.no_appointments_today')}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Popular Services */}
          <Card>
            <CardHeader title={t('admin.popular_services')} subtitle={t('admin.popular_services_subtitle')} icon={<QrCode className="h-4 w-4" />} />
            <div className="h-60 p-2">
              {(dashboardData.sourceMix || []).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dashboardData.sourceMix} dataKey="value" nameKey="name" innerRadius={35} outerRadius={65} paddingAngle={2}>
                      {dashboardData.sourceMix.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend 
                      wrapperStyle={{ fontSize: '10px' }}
                      iconSize={6}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: "rgba(255,255,255,0.95)", 
                        border: "1px solid rgba(0,0,0,0.1)", 
                        color: "rgba(0,0,0,0.8)",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        fontSize: "11px"
                      }} 
                      labelStyle={{ 
                        color: "rgba(0,0,0,0.8)", 
                        fontWeight: 600,
                        fontSize: "11px"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 dark:text-zinc-400">
                  <div className="text-center">
                    <QrCode className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">{t('admin.no_data')}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>

        {/* Bookings & Activity */}
        <div>
          <SectionTitle>{t('admin.section_recent_activity')}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
            {/* Recent Bookings */}
          <Card className="lg:col-span-3">
            <CardHeader 
              title={t('admin.recent_bookings')} 
              subtitle={t('admin.recent_bookings_subtitle')} 
              icon={<CheckCircle2 className="h-5 w-5" />} 
              action={
                <button 
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-colors font-medium"
                >
                  <Download className="h-3.5 w-3.5" />
                  {t('admin.export_csv')}
                </button>
              } 
            />
            <div className="p-3 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
                    <th className="py-2 px-2 font-semibold">{t('admin.col_client')}</th>
                    <th className="py-2 px-2 font-semibold">{t('admin.col_service')}</th>
                    <th className="py-2 px-2 font-semibold">{t('admin.col_barber')}</th>
                    <th className="py-2 px-2 font-semibold">{t('admin.col_date_time')}</th>
                    <th className="py-2 px-2 font-semibold">{t('admin.col_price')}</th>
                    <th className="py-2 px-2 font-semibold">{t('admin.col_status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5 dark:divide-white/10">
                  {(dashboardData.recentBookings || []).slice(0, 10).map((r) => (
                    <tr key={r.id} className="text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                      <td className="py-2 px-2 font-medium">{r.client?.name || r.client?.email || 'Unknown'}</td>
                      <td className="py-2 px-2 text-zinc-600 dark:text-zinc-300">{typeof r.service === 'object' ? (r.service?.name || r.Service?.name || r.name || 'Unknown') : (r.Service?.name || r.service || 'Unknown')}</td>
                      <td className="py-2 px-2">{r.barber?.name || r.barber || 'N/A'}</td>
                      <td className="py-2 px-2">
                        {(r.start_local || r.start_time || r.start_utc) ? (
                          <>
                            <div className="font-medium">{new Date(r.start_local || r.start_time || r.start_utc).toLocaleDateString()}</div>
                            <div className="text-zinc-500 dark:text-zinc-400">{new Date(r.start_local || r.start_time || r.start_utc).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                          </>
                        ) : (
                          <span className="text-zinc-400">N/A</span>
                        )}
                      </td>
                      <td className="py-2 px-2 font-bold">
                        {(() => {
                          let price = 0;
                          if (typeof r.price === 'number') {
                            price = r.price;
                          } else if (r.service?.price_cents) {
                            price = r.service.price_cents / 100;
                          } else if (r.Service?.price_cents) {
                            price = r.Service.price_cents / 100;
                          } else if (r.price_cents) {
                            price = r.price_cents / 100;
                          } else if (r.service_price) {
                            price = r.service_price / 100;
                          }
                          return `$${price.toFixed(2)}`;
                        })()}
                      </td>
                      <td className="py-2 px-2"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* AI Activity */}
          <Card className="lg:col-span-1">
            <CardHeader 
              title={t('admin.nav_ai_tools')} 
              subtitle={t('admin.recent_bookings_subtitle')} 
              icon={<Sparkles className="h-5 w-5" />}
              action={
                <button 
                  onClick={() => navigate('/admin/ai-analysis')}
                  className="text-xs px-3 py-1.5 rounded-lg border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-colors font-medium"
                >
                  View All
                </button>
              }
            />
            <div className="p-3 space-y-2 max-h-96 lg:max-h-[600px] overflow-y-auto">
              {(dashboardData.aiActivity || []).length > 0 ? (dashboardData.aiActivity || []).map((activity) => (
                <div 
                  key={activity.id} 
                  onClick={() => navigate('/admin/ai-analysis')}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors border border-black/5 dark:border-white/10 cursor-pointer group"
                >
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: `${ACCENT}20` }}>
                    <Sparkles className="h-4 w-4" style={{ color: ACCENT }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">{activity.tool}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        activity.status === 'completed' || activity.status === 'succeeded' 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' 
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}>
                        {activity.credits}cr
                      </span>
                    </div>
                    <p className="text-xs text-zinc-900 dark:text-zinc-100 font-medium truncate">{activity.client}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{activity.timeAgo}</p>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
                  <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('admin.no_recent_ai')}</p>
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>
      </div>
    </div>
  );
}